import { profileData } from '@/mocks/profile';
import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    const formData = new FormData(form);
    formData.delete('phone_alt');
    try {
      const response = await fetch('https://readdy.ai/api/form/d9u59vt3pcjqs2fcsmcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <Layout>
      <div className="pt-24 md:pt-28 pb-14 md:pb-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-[1100px] mx-auto">
          <Breadcrumb />

          <div className="mt-6 mb-12">
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-4">关于我们</h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl leading-relaxed">
              佐佐木明希官方粉丝站，致力于为全球粉丝提供最新、最全的佐佐木明希相关信息，打造温馨有爱的粉丝互动平台。
            </p>
          </div>

          <section className="mb-16" aria-labelledby="brand-title">
            <h2 id="brand-title" className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8">品牌介绍</h2>
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="lg:w-1/2">
                <div className="w-full aspect-[4/3] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Japanese%20actress%20elegant%20artistic%20portrait%20in%20minimalist%20white%20studio%2C%20warm%20natural%20lighting%2C%20cream%20and%20soft%20rose%20tones%2C%20professional%20editorial%20photography%2C%20clean%20sophisticated%20composition%2C%20gentle%20confident%20expression&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                    alt="佐佐木明希 品牌形象"
                    className="w-full h-full object-cover object-top"
                    title="佐佐木明希品牌形象写真"
                  />
                </div>
              </div>
              <div className="lg:w-1/2">
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  佐佐木明希（佐々木 明希 / Aki Sasaki），1992年3月15日出生于日本东京都。自幼在艺术氛围浓厚的家庭中成长，6岁开始学习钢琴，10岁接触日本传统舞踊，这些早期的艺术熏陶为她日后的演艺事业奠定了坚实基础。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  2014年，佐佐木明希以电视剧《青春的诗》正式出道，凭借自然灵动的演技迅速获得业界关注。此后，她在影视、综艺、舞台剧、广告代言等多个领域全面发展，逐步成长为日本最具影响力的艺人之一。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  佐佐木明希以其温暖治愈的笑容、真诚亲和的态度以及对待工作的专业精神，赢得了无数粉丝的喜爱与尊重。她不仅在演艺事业上持续突破，更积极投身公益事业，用自身影响力传递正能量，成为许多年轻人心中温暖而坚定的榜样。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  面向未来，佐佐木明希希望继续挑战更多不同类型的角色，同时尝试编剧和导演等幕后创作工作，以更多元的方式表达自己的艺术理念。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-16" aria-labelledby="honors-title">
            <h2 id="honors-title" className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8">荣誉与口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <h3 className="font-heading text-lg text-foreground-900 mb-5">主要荣誉</h3>
                <ul className="space-y-4">
                  {profileData.achievements.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 shrink-0 mt-0.5">
                        <i className="ri-medal-line text-accent-600 text-sm"></i>
                      </span>
                      <span className="text-sm text-foreground-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <h3 className="font-heading text-lg text-foreground-900 mb-5">业界评价</h3>
                <div className="space-y-5">
                  {[
                    { quote: '她拥有一种罕见的天赋——能让每个角色都变得真实而动人。', source: '—— 是枝裕和（导演）' },
                    { quote: '佐佐木小姐的敬业精神和对细节的追求令人敬佩，她是年轻一代演员中的标杆。', source: '—— 山田洋次（导演）' },
                    { quote: '她用真诚打动了每一个人，这正是她最珍贵的魅力所在。', source: '—— 《VOGUE Japan》编辑部' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <i className="ri-double-quotes-l text-primary-400 text-lg shrink-0 mt-0.5"></i>
                      <div>
                        <p className="text-sm text-foreground-700 leading-relaxed">{item.quote}</p>
                        <p className="text-xs text-foreground-400 mt-1">{item.source}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16" aria-labelledby="stats-title">
            <h2 id="stats-title" className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8">数据一览</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { value: '580万+', label: '社交媒体粉丝数' },
                { value: '45部+', label: '参演影视作品' },
                { value: '12个', label: '品牌代言' },
                { value: '30场+', label: '公益活动参与' },
                { value: '15本', label: '杂志封面' },
                { value: '8座', label: '行业奖项' },
                { value: '20场+', label: '粉丝见面会' },
                { value: '3本', label: '个人写真集' },
              ].map((item) => (
                <div key={item.label} className="bg-background-100 rounded-lg p-5 text-center">
                  <p className="font-heading text-2xl md:text-3xl text-primary-500 mb-1">{item.value}</p>
                  <p className="text-xs md:text-sm text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16" id="contact" aria-labelledby="contact-title">
            <h2 id="contact-title" className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-50 shrink-0">
                      <i className="ri-mail-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-500">contact@sasakinoaki-fans.com</p>
                      <p className="text-sm text-foreground-500">press@sasakinoaki-fans.com（媒体合作）</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-50 shrink-0">
                      <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">办公地址</h4>
                      <p className="text-sm text-foreground-500">〒107-0052</p>
                      <p className="text-sm text-foreground-500">日本东京都港区赤坂1-12-32 ARK Hills 15F</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-50 shrink-0">
                      <i className="ri-phone-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">电话</h4>
                      <p className="text-sm text-foreground-500">+81 3-1234-5678</p>
                      <p className="text-sm text-foreground-500">工作时间：周一至周五 10:00-18:00（日本时间）</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-sm font-semibold text-foreground-900 mb-4">关注我们</h4>
                  <div className="flex items-center gap-3">
                    {[
                      { icon: 'ri-twitter-x-line', href: profileData.socialLinks.twitter, label: 'Twitter' },
                      { icon: 'ri-instagram-line', href: profileData.socialLinks.instagram, label: 'Instagram' },
                      { icon: 'ri-youtube-line', href: profileData.socialLinks.youtube, label: 'YouTube' },
                      { icon: 'ri-weibo-line', href: profileData.socialLinks.weibo, label: '微博' },
                    ].map((social) => (
                      <a
                        key={social.label}
                        href={social.href}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-all duration-300"
                        aria-label={social.label}
                      >
                        <i className={`${social.icon} text-sm`}></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-heading text-lg text-foreground-900 mb-6">发送消息</h3>
                <form
                  action="https://readdy.ai/api/form/d9u59vt3pcjqs2fcsmcg"
                  method="POST"
                  className="space-y-5"
                  data-readdy-form="contact"
                  onSubmit={handleSubmit}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">姓名</label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">邮箱</label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-2">主题</label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">消息内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的消息（最多500字符）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">最多500字符</p>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />
                  <button
                    type="submit"
                    className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 mt-3">感谢您的留言！我们将尽快回复。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 mt-3">{formError || '提交失败，请稍后重试'}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}